import { useMemo, useState } from 'react';
import { z } from 'zod';
import {
    CommanderFormProps,
    SchemaFormDefinition,
    SchemaFormFields,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { BusinessError, createZodFormResolver, getBase64, getDefaults, lt, Validation } from '@mews/framework';
import { ChangeFileField, ChangeFileFieldProps } from '../../../common/components/ChangeFileField';
import { FILE_SCHEMA_OPTIONS } from '../../../common/constants';
import { useGetServiceNamesByIdQuery } from '../../../common/queries/useGetServiceNamesByIdQuery';
import { useUpdateAttachmentMutation } from '../hooks/useUpdateAttachmentMutation';
import { useOpenAttachmentInNewTab } from '../hooks/useOpenAttachmentInNewTab';
import { useCurrentEnterpriseAttachments } from '../../../common/hooks/useCurrentEnterpriseAttachments';
import { useAttachmentLibraryAttachment } from '../hooks/useAttachmentLibraryAttachment';

const editFileSchema = (context: { isNewFile: boolean; existingNames: string[] }) =>
    z.object({
        file: context.isNewFile
            ? Validation.File.Default(FILE_SCHEMA_OPTIONS)
            : Validation.File.Default(FILE_SCHEMA_OPTIONS).optional(),
        name: z
            .string()
            .min(1)
            .max(255)
            .refine((name) => !context.existingNames.includes(name), {
                message: 'ContractFormDuplicateNameValidationMessage',
            }),
        services: z.array(z.string()).min(1),
        notes: z.string().max(1000).optional(),
    });

export type EditFileFormValues = z.infer<ReturnType<typeof editFileSchema>>;

interface EditFileFormDefinitionProps extends Omit<ChangeFileFieldProps, 'fieldProps'> {
    services: Record<string, string>;
}

const editFileFormDefinition = (
    props: EditFileFormDefinitionProps
): SchemaFormDefinition<ReturnType<typeof editFileSchema>> => ({
    fields: {
        file: {
            label: lt('File'),
            render: (fieldProps) => <ChangeFileField {...props} fieldProps={fieldProps} />,
            required: true,
        },
        name: {
            label: lt('ContractsAttachmentLibraryCustomFileName'),
            required: true,
        },
        services: {
            label: lt('ContractsAttachmentLibraryApplicableServices'),
            field: {
                type: 'select',
                options: Object.entries(props.services).map(([value, label]) => createOption({ value, label })),
            },
            required: true,
        },
        notes: {
            label: lt('Note'),
            field: {
                type: 'textarea',
                rows: 1,
            },
        },
    },
});

interface EditFileFormProps extends CommanderFormProps {
    attachmentId: string;
}

export const EditFileForm = ({ attachmentId, ...formProps }: EditFileFormProps) => {
    const [isNewFile, setIsNewFile] = useState(false);
    const { data: services } = useGetServiceNamesByIdQuery();
    const { mutate: openAttachment, isPending } = useOpenAttachmentInNewTab();
    const { mutateAsync: updateAttachmentMutation } = useUpdateAttachmentMutation();
    const attachments = useCurrentEnterpriseAttachments();

    const attachment = useAttachmentLibraryAttachment({ attachmentId });

    const schema = useMemo(() => {
        const existingNames =
            attachments?.filter(({ id }) => id !== attachmentId).map(({ customName }) => customName) || [];
        return editFileSchema({ isNewFile, existingNames });
    }, [attachments, attachmentId, isNewFile]);

    if (attachment == null) {
        throw new BusinessError('Attachment not found');
    }

    const { renderFormContent } = useHandledDialogFormControls<EditFileFormValues>({
        ...formProps,
        defaultValues: getDefaults(schema),
        resolver: createZodFormResolver(schema),
        onSubmit: async (values) => {
            const fileItem = values.file?.[0];
            const base64Data = fileItem ? await getBase64(fileItem) : undefined;
            const parameters = {
                id: attachment.id,
                file:
                    fileItem != null && base64Data != null
                        ? {
                              value: {
                                  name: fileItem.name,
                                  mimeType: fileItem.type,
                                  data: base64Data,
                              },
                          }
                        : undefined,
                customName: values.name,
                serviceIds: values.services,
                notes: values.notes,
            };

            await updateAttachmentMutation(parameters);
        },
        values: {
            name: attachment.customName,
            services: attachment.services.map(({ id }) => id),
            notes: attachment.notes ?? undefined,
        },
    });

    return renderFormContent(
        <SchemaFormFields
            schema={schema}
            formDefinition={editFileFormDefinition({
                fileName: attachment.fileName,
                fileId: attachment.id,
                isNewFile,
                setIsNewFile,
                services,
                openFile: openAttachment,
                isPending,
            })}
        />
    );
};
